import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Image,
  Platform,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { Actions, GiftedChat, IMessage, Send } from "react-native-gifted-chat";
import { useAppSelector } from "../../redux/store";
import {
  addDoc,
  collection,
  db,
  doc as firestoreDoc,
  DocumentData,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  Timestamp,
  where,
} from "../../firebase";
import {
  ActivityIndicator,
  Icon,
  IconButton,
  useTheme,
} from "react-native-paper";
import * as ImagePicker from "expo-image-picker";
import { CommonActions } from "@react-navigation/native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../types/navigationParams";
import { generateThumbnail, uploadMediaAsync } from "../../services/storage";
import { useHeaderHeight } from "@react-navigation/elements";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";
import { Collection as CollectionConfig } from "../../config/companies/config";

type Props = NativeStackScreenProps<RootStackParamList, "ChatScreen">;

interface CustomMessage extends IMessage {
  thumbnail: string;
}

const ChatScreen = ({ route, navigation }: Props) => {
  const chatId = route.params?.chatId;
  const detailsCollectionName = route.params?.collection;
  const title = route.params?.title;
  const [messages, setMessages] = useState<CustomMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<{
    uri: string;
    type: "image" | "video";
  } | null>(null);
  const currentUser = useAppSelector((state) => state.auth.user);
  const config = useAppSelector((state) => state.auth.config);
  const companyId = currentUser.company?.id || "";
  const theme = useTheme();
  const headerHeight = useHeaderHeight();
  const { t } = useTranslation();

  useEffect(() => {
    if (title) {
      navigation.setOptions({ title });
    }
  }, [title, navigation]);

  useEffect(() => {
    if (!chatId || !detailsCollectionName) {
      return;
    }

    const state = navigation.getState();
    const previousRoute = state.routes[state.index - 1];

    if (previousRoute?.name === "DetailsScreen") {
      return;
    }

    const resolveDetailsCollection = async (): Promise<{
      collection: CollectionConfig;
      data: Record<string, any>;
    } | null> => {
      const collectionConfig = config.database.find(
        (item) => item.name === detailsCollectionName,
      );

      if (!collectionConfig) {
        return null;
      }

      const documentSnapshot = await getDoc(
        firestoreDoc(db, collectionConfig.name, chatId),
      );

      if (documentSnapshot.exists()) {
        return {
          collection: collectionConfig,
          data: {
            ...documentSnapshot.data(),
            id: documentSnapshot.id,
          },
        };
      }

      return null;
    };

    let cancelled = false;

    void resolveDetailsCollection()
      .then((details) => {
        if (!details || cancelled) {
          return;
        }

        const currentState = navigation.getState();
        const currentRoute = currentState.routes[currentState.index];
        const latestPreviousRoute = currentState.routes[currentState.index - 1];

        if (
          currentRoute?.name !== "ChatScreen" ||
          latestPreviousRoute?.name === "DetailsScreen"
        ) {
          return;
        }

        const routesBeforeChat = currentState.routes.slice(
          0,
          currentState.index,
        );
        const nextRoutes = [
          ...routesBeforeChat,
          {
            name: "DetailsScreen",
            params: {
              fields: details.collection.fields,
              data: details.data,
              collection: details.collection,
            },
          },
          currentRoute,
        ];

        navigation.dispatch(
          CommonActions.reset({
            ...currentState,
            routes: nextRoutes,
            index: nextRoutes.length - 1,
          }),
        );
      })
      .catch((error) => {
        console.error("Failed to resolve chat details screen:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [chatId, config.database, detailsCollectionName, navigation]);

  useEffect(() => {
    const messagesRef = collection(db, "messages");
    const q = query(
      messagesRef,
      where("chatId", "==", chatId),
      orderBy("createdAt", "desc"),
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedMessages = snapshot.docs.map(
        (doc: QueryDocumentSnapshot<DocumentData>) => {
          const data = doc.data();
          return {
            _id: doc.id,
            text: data.text,
            createdAt: data.createdAt.toDate(),
            user: data.user,
            image: data.image || null,
            video: data.video || null,
            thumbnail: data.thumbnail || null,
          };
        },
      );
      setMessages(fetchedMessages);
    });

    return () => unsubscribe();
  }, [chatId]);

  const pickMedia = async () => {
    try {
      const { granted } =
        await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!granted) {
        Alert.alert(t("Access denied"), t("Gallery permission is required"));
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images", "videos"],
        quality: 0.5,
      });

      if (!result.canceled && result.assets?.length > 0) {
        const asset = result.assets[0];
        const mediaType = asset.type === "video" ? "video" : "image";
        setSelectedMedia({ uri: asset.uri, type: mediaType });
      }
    } catch (error) {
      if (error instanceof Error) {
        Alert.alert(t("Error"), error.message);
      }
    }
  };

  const onSend = useCallback(
    async (newMessages: CustomMessage[] = []) => {
      const message = newMessages[0];
      const messagesRef = collection(db, "messages");

      let mediaUrl = "";
      let thumbnailUrl = "";
      if (selectedMedia) {
        try {
          if (!companyId) {
            throw new Error("Company ID is required");
          }

          setLoading(true);
          mediaUrl = await uploadMediaAsync(
            selectedMedia.uri,
            selectedMedia.type,
            companyId,
          );
          const thumbnail = await generateThumbnail(selectedMedia.uri);
          thumbnailUrl = await uploadMediaAsync(thumbnail, "image", companyId);
        } catch {
          Alert.alert(t("Upload error"), t("Failed to upload media file."));
        } finally {
          setLoading(false);
        }
      }

      await addDoc(messagesRef, {
        text: message.text,
        createdAt: Timestamp.now(),
        user: message.user,
        chatId,
        image: selectedMedia?.type === "image" ? mediaUrl : "",
        video: selectedMedia?.type === "video" ? mediaUrl : "",
        thumbnail: selectedMedia?.type === "video" ? thumbnailUrl : "",
      });

      setSelectedMedia(null);
    },
    [chatId, companyId, selectedMedia, t],
  );

  // useEffect(() => {
  //   alert(headerHeight);
  // }, [headerHeight]);

  return (
    <SafeAreaView style={{ flex: 1 }} edges={["bottom"]}>
      <GiftedChat
        renderAvatar={() => <View style={{ height: 36, width: 36 }} />}
        isAvatarOnTop={false}
        isSendButtonAlwaysVisible
        messages={messages}
        onSend={(messages) => onSend(messages)}
        user={{
          _id: currentUser?.uid || "guest",
          name: currentUser?.fullName || "Guest",
          avatar: "https://i.pravatar.cc/150?img=4",
        }}
        renderMessageImage={(props) => (
          <TouchableOpacity
            onPress={() =>
              navigation.navigate("ImageScreen", {
                uri: props.currentMessage?.image!,
              })
            }
          >
            <Image
              style={{ width: 150, height: 150, borderRadius: 16 }}
              source={{ uri: props.currentMessage.image }}
            />
          </TouchableOpacity>
        )}
        renderMessageVideo={(props) => {
          const thumbnail = props.currentMessage.thumbnail;
          return (
            <View style={styles.videoMessageContainer}>
              <Image style={styles.thumbnail} source={{ uri: thumbnail }} />
              <IconButton
                icon="play"
                size={42}
                onPress={() =>
                  navigation.navigate("VideoScreen", {
                    videoSource: props.currentMessage.video!,
                  })
                }
                style={styles.playButton}
              />
            </View>
          );
        }}
        renderActions={(props) => (
          <Actions
            {...props}
            icon={() =>
              loading ? (
                <ActivityIndicator size="small" color={theme.colors.primary} />
              ) : (
                <Icon
                  source="attachment"
                  size={24}
                  color={theme.colors.primary}
                />
              )
            }
            onPressActionButton={pickMedia}
          />
        )}
        renderChatFooter={() =>
          selectedMedia && (
            <View style={styles.footerImageContainer}>
              <Pressable
                onPress={() => {
                  if (selectedMedia?.type === "video") {
                    return navigation.navigate("VideoScreen", {
                      videoSource: selectedMedia?.uri,
                    });
                  } else {
                    return navigation.navigate("ImageScreen", {
                      uri: selectedMedia?.uri,
                    });
                  }
                }}
                style={[
                  styles.footerImageWrapper,
                  {
                    backgroundColor: theme.colors.secondaryContainer,
                    borderRadius: 10,
                  },
                ]}
              >
                {selectedMedia.type === "image" ? (
                  <Image
                    resizeMode="cover"
                    source={{ uri: selectedMedia.uri }}
                    style={styles.previewImage}
                  />
                ) : (
                  <View
                    style={{
                      alignSelf: "center",
                    }}
                  >
                    <Icon size={36} source="video" />
                  </View>
                )}
                <IconButton
                  icon="close"
                  size={14}
                  onPress={() => setSelectedMedia(null)}
                  iconColor={theme.colors.error}
                  containerColor={theme.colors.errorContainer}
                  style={styles.closeButton}
                />
              </Pressable>
            </View>
          )
        }
        renderSend={(props) => (
          <Send
            {...props}
            containerStyle={styles.sendButtonContainer}
            sendButtonProps={{
              enabled: !loading,
            }}
          >
            <Icon
              testID="messageSend"
              source="send-circle"
              size={36}
              color={theme.colors.primary}
            />
          </Send>
        )}
        // keyboardAvoidingViewProps={{ keyboardVerticalOffset: headerHeight + insets.bottom }}
        keyboardAvoidingViewProps={{
          keyboardVerticalOffset:
            Platform.OS === "android" ? 100 : headerHeight,
        }}
        // keyboardAvoidingViewProps={{ keyboardVerticalOffset: 150 }}
      />
      {/*{Platform.OS === "android" && <KeyboardAvoidingView behavior="padding" />}*/}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  footerImageContainer: {
    height: 100,
    padding: 10,
  },
  footerImageWrapper: {
    width: 90,
    height: "100%",
    backgroundColor: "transparent",
    justifyContent: "center",
  },
  previewImage: {
    borderRadius: 8,
    width: "100%",
    height: "100%",
  },
  closeButton: {
    position: "absolute",
    right: -13,
    top: -13,
    width: 20,
    height: 20,
  },
  sendButtonContainer: {
    justifyContent: "center",
    alignItems: "center",
    marginRight: 5,
  },
  thumbnail: {
    width: "100%",
    height: "100%",
  },
  videoMessageContainer: {
    borderRadius: 16,
    width: 160,
    height: 150,
  },
  playButton: {
    position: "absolute",
    alignSelf: "center", // Горизонтальное центрирование
    top: "50%", // Вертикальное центрирование
    marginTop: -21, // Смещение на половину размера иконки
  },
});

export default ChatScreen;
